import {
  EMAIL_PATTERN,
  MIDDLE_NAME_PATTERN,
  NAME_PATTERN,
  PHONE_MASK,
  PHONE_PATTERN,
  ZIP_CODE_PATTERN
} from '../../core/constants/validation.constants';
import {
  FieldModeRules,
  FieldType,
  FormFieldConfig,
  FormMode
} from '../../shared/components/dynamic-form/form-field.model';

/** The envelope both dynamic-form endpoints answer with. */
export interface DynamicFormEnvelope<T> {
  data: T | null;
  success: boolean;
  statusCode: number;
  message: string | null;
  errorDetails: string | null;
}

/** One screen a role can open, from Role/GetFeaturesByRoleId. Its `featureUuid`
 *  is what the field-configuration call is keyed by. */
export interface FeatureItem {
  id: number;
  featureUuid: string;
  featureName: string;
  description: string | null;
  isActive: boolean;
  roleUuid: string;
}

export interface GetFeaturesByRoleRequest {
  roleUuid: string;
}

export interface DynamicFormRequest {
  featureUuid: string;
  /** Scopes the show/role mapping to one show, e.g. `sa` or `hdgs`. Omitted for
   *  show-agnostic forms, which then get whatever mappings exist. */
  showCode?: string;
}

/** One configured field from DynamicForm/GetDynamicformByFeautreUuid.
 *
 *  The show/role mapping is LEFT JOINed server-side, so every property the
 *  mapping owns comes back `null` when a field has no mapping row for this
 *  show + role — hence the nullable types. Audit columns are omitted; nothing
 *  renders them. */
export interface DynamicFieldResponse {
  id: number;
  dynamicFieldUuid: string;
  featureUuid: string;
  fieldKey: string;
  label: string;
  fieldType: string;
  isActive: boolean;
  dynamicFieldShowRoleMappingUuid: string | null;
  showUuid: string | null;
  roleUuid: string | null;
  displayOrder: number | null;
  minValue: number | null;
  maxValue: number | null;
  isRequired: boolean | null;
  isDisplayed: boolean | null;
  isDisabled: boolean | null;
  /** Edit-mode overrides of the three flags above. Only `true` counts — the
   *  columns default to false, so `false` / `null` inherit the add value. */
  isRequiredForEdit: boolean | null;
  isDisplayedForEdit: boolean | null;
  isDisabledForEdit: boolean | null;
  fullWidth: boolean | null;
  pattern: string | null;
  mask: string | null;
  keyFilter: string | null;
  lookup: string | null;
  options: string | null;
  requiredMessage: string | null;
  minValueMessage: string | null;
  maxValueMessage: string | null;
  patternMessage: string | null;
  showMappingIsActive: boolean | null;
}

/** Shared patterns the API can name instead of shipping a regex. */
const PATTERNS: Record<string, RegExp> = {
  EMAIL_PATTERN,
  PHONE_PATTERN,
  ZIP_CODE_PATTERN,
  NAME_PATTERN,
  MIDDLE_NAME_PATTERN
};

/** Shared masks the API can name instead of shipping a mask string. */
const MASKS: Record<string, string> = { PHONE_MASK };

/** Types the engine has a control for. */
const RENDERABLE = new Set<string>(['text', 'number', 'select', 'date', 'textarea', 'mask', 'esign', 'upload']);

/** Types the API may send before a dedicated control exists — rendering them as
 *  the nearest built-in beats dropping the field. */
const TYPE_ALIASES: Record<string, FieldType> = { email: 'text', string: 'text', int: 'number' };

/** Field order once the API leaves `displayOrder` unset — unordered fields sort
 *  after configured ones instead of turning the comparison into `NaN`. */
const UNORDERED = Number.MAX_SAFE_INTEGER;

/** A field's rules in each mode. Every flag the mapping owns is nullable, so
 *  absence reads as "not restricted" — only an explicit `false` hides a field.
 *  An edit flag only ever switches a rule on; anything but `true` inherits the
 *  add rule. */
function toModeRules(field: DynamicFieldResponse): Record<FormMode, FieldModeRules> {
  const add: FieldModeRules = {
    required: field.isRequired === true,
    disabled: field.isDisabled === true,
    hidden: field.isDisplayed === false
  };
  return {
    add,
    edit: {
      required: field.isRequiredForEdit === true || add.required,
      disabled: field.isDisabledForEdit === true || add.disabled,
      hidden: field.isDisplayedForEdit === true ? false : add.hidden
    }
  };
}

/** A configured field shown in at least one mode for this show + role. One
 *  hidden in a single mode is kept and carries `hidden` for that mode. */
function isVisible(field: DynamicFieldResponse): boolean {
  if (!field.isActive || field.showMappingIsActive === false) {
    return false;
  }
  const { add, edit } = toModeRules(field);
  return !add.hidden || !edit.hidden;
}

/** Maps the API's `fieldType` to a control the registry can render. */
function toFieldType(fieldType: string): FieldType {
  const type = (fieldType ?? '').toLowerCase();
  return RENDERABLE.has(type) ? (type as FieldType) : (TYPE_ALIASES[type] ?? 'text');
}

/** Same keys, but only the ones the API actually set — an unset property would
 *  otherwise overwrite an engine default with `null` or a blank message. `0` is
 *  a real bound, so only null/undefined and empty strings drop out. */
type Configured<T> = { [K in keyof T]?: NonNullable<T[K]> };

function configured<T extends object>(values: T): Configured<T> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value != null && value !== '')
  ) as Configured<T>;
}

/**
 * A date bound as the configuration ships it: `DDMMYYYY` packed into a number,
 * with the leading zero dropped — `31082018` is 31 August 2018. Some rows were
 * seeded `MMDDYYYY` (`8312018`), so that order is the fallback when `DDMMYYYY`
 * isn't a real date.
 *
 * Built in local time, which is how the picker hands its own value back and how
 * the birth-date constants are written.
 */
function toDateBound(value: number | null, fieldKey: string): Date | null {
  if (value == null) {
    return null;
  }

  const digits = `${value}`.padStart(8, '0');
  const date = digits.length === 8 ? (packedDate(digits, 'dmy') ?? packedDate(digits, 'mdy')) : null;
  if (!date) {
    console.warn(`[dynamic-form] Ignoring unreadable date bound "${value}" for "${fieldKey}"`);
  }
  return date;
}

function packedDate(digits: string, order: 'dmy' | 'mdy'): Date | null {
  const first = Number(digits.slice(0, 2));
  const second = Number(digits.slice(2, 4));
  const [day, month] = order === 'dmy' ? [first, second] : [second, first];
  const year = Number(digits.slice(4));
  const date = new Date(year, month - 1, day);

  // The last check rejects a day the month doesn't have: `new Date(2007, 1, 30)`
  // rolls forward into March rather than failing.
  return year < 1900 || month < 1 || month > 12 || date.getMonth() !== month - 1 ? null : date;
}

/** `minValue` / `maxValue` bound a date's day, a number's value, or a text
 *  field's length, depending on what the field renders as. */
function toBounds(field: DynamicFieldResponse, type: FieldType): Configured<FormFieldConfig> {
  const { minValue: min, maxValue: max, fieldKey } = field;
  if (type === 'date') {
    return configured({
      minDate: toDateBound(min, fieldKey),
      maxDate: toDateBound(max, fieldKey)
    });
  }
  return type === 'number'
    ? configured({ min, max })
    : configured({ minLength: min, maxLength: max });
}

/** `minValueMessage` / `maxValueMessage` belong to whichever rule {@link toBounds}
 *  put the bounds on — a text field's length errors read the length messages. */
function toBoundMessages(field: DynamicFieldResponse, type: FieldType): Configured<FormFieldConfig> {
  const { minValueMessage: min, maxValueMessage: max } = field;
  return type === 'number' || type === 'date'
    ? configured({ minMessage: min, maxMessage: max })
    : configured({ minLengthMessage: min, maxLengthMessage: max });
}

/** Inline `options`, which the API ships as a JSON string. */
function toOptions(field: DynamicFieldResponse): unknown[] | null {
  if (!field.options) {
    return null;
  }
  try {
    return JSON.parse(field.options);
  } catch (e) {
    console.warn(`[dynamic-form] Ignoring unparseable options for "${field.fieldKey}":`, e);
    return null;
  }
}

/** Spells a configured date range out in the field's tooltip, so the range the
 *  picker enforces is also one the user can read before tripping over it. */
function toRangeHelp(bounds: Configured<FormFieldConfig>): string | null {
  const { minDate, maxDate } = bounds;
  const day = (date: Date) =>
    date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  if (minDate && maxDate) {
    return `Eligible dates: ${day(minDate)} - ${day(maxDate)}`;
  }
  if (minDate) {
    return `Eligible dates: ${day(minDate)} onwards`;
  }
  return maxDate ? `Eligible dates: up to ${day(maxDate)}` : null;
}

/** Turns one configured field into the engine's render + validation config. */
function toFieldConfig(field: DynamicFieldResponse): FormFieldConfig {
  const type = toFieldType(field.fieldType);
  const bounds = toBounds(field, type);
  const modes = toModeRules(field);

  return {
    key: field.fieldKey,
    label: field.label,
    type,
    ...modes.add,
    modes,
    fullWidth: field.fullWidth === true,
    ...bounds,
    ...toBoundMessages(field, type),
    ...configured({
      helpText: type === 'date' ? toRangeHelp(bounds) : null,
      lookup: field.lookup,
      keyFilter: field.keyFilter,
      pattern: field.pattern ? PATTERNS[field.pattern] ?? field.pattern : null,
      mask: field.mask ? MASKS[field.mask] ?? field.mask : null,
      options: toOptions(field),
      // Configured copy wins over the engine's generated message.
      requiredMessage: field.requiredMessage,
      patternMessage: field.patternMessage
    })
  };
}

/** One row per field. The API joins both the show-specific and the global
 *  (`showUuid: null`) mapping, so a field configured at both levels arrives
 *  twice — the show-specific row wins, and a second control for the same key
 *  never reaches the form.
 *
 *  Keys are matched case-insensitively because the API reads them that way
 *  (`TryGetPropertyIgnoreCase`): two spellings of one key would be a single
 *  value server-side, so rendering both would put two controls on one field. */
function deduplicate(fields: DynamicFieldResponse[]): DynamicFieldResponse[] {
  const byKey = new Map<string, DynamicFieldResponse>();
  for (const field of fields) {
    const key = (field.fieldKey ?? '').trim().toLowerCase();
    const kept = byKey.get(key);
    if (!kept || (!kept.showUuid && field.showUuid)) {
      byKey.set(key, field);
    }
  }
  return [...byKey.values()];
}

/** The visible fields of a form, in configured order, ready for the engine. */
export function mapDynamicFields(fields: DynamicFieldResponse[]): FormFieldConfig[] {
  return deduplicate(fields.filter(isVisible))
    .sort((a, b) => (a.displayOrder ?? UNORDERED) - (b.displayOrder ?? UNORDERED))
    .map(toFieldConfig);
}

/** A value the user still has to supply. Mirrors `Validators.required`. */
function isBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

/** Whether a saved record satisfies every field the config marks required.
 *  Completion is the form's own rule, so it has to come from the same config
 *  that renders the asterisks rather than a second hardcoded list. */
export function hasRequiredValues(
  values: Record<string, unknown> | null | undefined,
  fields: FormFieldConfig[]
): boolean {
  if (!values || fields.length === 0) {
    return false;
  }
  return fields.every((field) => !field.required || field.hidden || !isBlank(values[field.key]));
}
